import classNames from 'classnames';
import * as React from 'react';
import Select, { components, OptionProps } from 'react-select';
import FakePicker from '../FakePicker/FakePicker';
import { Icons } from 'src/assets/fonts/Icons/icons';
import { allOptionsValue } from 'src/constants/main';
import { localeStore } from 'src/mobx/localesStore';
import { getDropdownHeights } from 'src/utils/styles';
import Icon from 'src/view/components/Controls/Icon/Icon';
import { IPickerOption } from 'src/view/components/Controls/Pickers/Picker/types/index';
import './style.scss';

interface IProps {
  className?: string;
  inputClassName?: string;
  wrapperClassName?: string;
  options: IPickerOption[];
  emptyListText?: string;
  selectedOptions?: IPickerOption[];
  defaultOptions?: IPickerOption[];
  isMulti?: boolean;
  isSearchable?: boolean;
  optionsDesc?: string; // e.g. branches | areas | items
  placeholder?: string; // e.g. branches | areas | items
  onSelect: (options: IPickerOption[]) => void;
}

interface IState {
  selectedOptions: IPickerOption[];
  isOpen: boolean;
}

const PickerDropdown = ({ children, isOpen, target, onClose, className, wrapperClassName }: any) => (
  <div className={className}>
    {target}
    {isOpen ? <div className={classNames('custom-select__wrapper', wrapperClassName)}>{children}</div> : null}
    {isOpen ? <div className="custom-select__blanked" onClick={onClose} /> : null}
  </div>
);

const SearchPlaceholder = (props: any) => (
  // eslint-disable-next-line react/jsx-props-no-spreading
  <components.Placeholder {...props}>
    <div className="d-flex">
      <Icon name={Icons.Search} className="mr-4 color-gray2" />
      <div className="custom-select__search-placeholder">{localeStore.t('txt_search')}</div>
    </div>
  </components.Placeholder>
);

const CustomSelectOption = (props: OptionProps<any, any>) => (
  // eslint-disable-next-line react/jsx-props-no-spreading
  <components.Option {...props}>
    <div className="d-flex">
      <Icon name={Icons.Check} size={22} className={`ml-2 mr-4 color-mainblue ${!props.isSelected ? 'custom-select__check-hide' : ''}`} />
      <div className="family-medium align-self-center text-elipsis">{props.data.label}</div>
    </div>
  </components.Option>
);

export default class CustomSelect extends React.Component<IProps, IState> {
  state: IState = {
    selectedOptions: this.props.selectedOptions || [],
    isOpen: false,
  };

  componentDidMount() {
    const selectedOptions = this.markSelected(this.state.selectedOptions || this.props.defaultOptions);
    this.setState({ selectedOptions });
  }

  markSelected = (options: IPickerOption[]): IPickerOption[] => {
    let selectedOptions: IPickerOption[] = this.state.selectedOptions;

    if (!options.length && this.props.defaultOptions) return this.props.defaultOptions;

    if (this.props.isMulti) {
      const allOption = options.find((option) => option.value === allOptionsValue);
      if (allOption && options.indexOf(allOption) === 0 && options.length > 1) {
        selectedOptions = options.filter((option) => option.value !== allOptionsValue);
      } else if (allOption) {
        selectedOptions = [allOption];
      } else {
        selectedOptions = options;
      }
    } else {
      selectedOptions = options;
    }

    return selectedOptions;
  };

  get placeholder() {
    if (this.props.placeholder && this.state.selectedOptions?.[0]?.value === this.props.defaultOptions?.[0]?.value) return this.props.placeholder;
    if (!this.state.selectedOptions.length) return localeStore.t(`select_${this.props.optionsDesc || 'items'}`);
    if (this.props.isMulti) {
      const selectedAllOption = this.state.selectedOptions.find((option) => option.value === allOptionsValue);
      return selectedAllOption
        ? localeStore.t(selectedAllOption.label)
        : `${localeStore.t(`selected_${this.props.optionsDesc || 'items'}`)} (${this.state.selectedOptions.length})`;
    } else {
      const [option] = this.state.selectedOptions;
      return option.label;
    }
  }

  get customSelectComponents() {
    const selectComponents: any = {
      DropdownIndicator: () => null,
      Option: CustomSelectOption,
    };
    if (this.props.isSearchable) {
      selectComponents.Placeholder = SearchPlaceholder;
    } else {
      // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
      selectComponents.Control = function () {
        return null;
      };
    }
    return selectComponents;
  }

  get list() {
    const list = [...(this.props.defaultOptions || []), ...(this.props.selectedOptions || []), ...this.props.options];
    // remove duplicate options to avoid collision
    return [...new Map(list.map((opt) => [opt.value, opt])).values()];
  }

  get selectStyles() {
    return {
      menuList: (provided: any) => ({
        ...provided,
        ...getDropdownHeights(this.props.options.length),
        borderRadius: this.props.isSearchable ? '0 !important' : '12px 12px 0 0',
      }),
    };
  }

  toggleOpen = () => {
    this.setState((state) => ({ isOpen: !state.isOpen }));
  };

  onSelect = (options: any) => {
    const selectedOptions = this.markSelected(this.props.isMulti ? options : [options]);
    this.setState({ selectedOptions }, () => this.props.onSelect(this.state.selectedOptions));
  };

  noOptionsMessage = () => {
    return this.props.emptyListText || localeStore.t('no_options');
  };

  public render() {
    return (
      <PickerDropdown
        isOpen={this.state.isOpen}
        onClose={this.toggleOpen}
        className={this.props.className || ''}
        wrapperClassName={this.props.wrapperClassName || ''}
        target={
          <FakePicker value={this.placeholder} className={this.props.inputClassName || ''} textClassName="family-medium" onClick={this.toggleOpen} />
        }
      >
        <Select
          autoFocus
          classNamePrefix="custom-select"
          styles={this.selectStyles}
          value={this.state.selectedOptions}
          options={this.list}
          onChange={this.onSelect}
          isMulti={this.props.isMulti}
          closeMenuOnSelect={false}
          noOptionsMessage={this.noOptionsMessage}
          hideSelectedOptions={false}
          controlShouldRenderValue={false}
          isClearable={false}
          components={this.customSelectComponents}
          menuIsOpen
        />
      </PickerDropdown>
    );
  }
}
